/**
 * Ritual Live Notification Component
 * Displays a notification when a ritual goes live
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getRitualTypeLabel } from "@/features/rituals/types"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface RitualLiveNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface RitualLiveNotificationData {
  ritualId?: string
  ritualName?: string
  ritualCoverImageUrl?: string | null
  isPaid?: boolean
  fee?: number | null
  duration?: string
  ritualType?: string
  durationType?: string
}

export function RitualLiveNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: RitualLiveNotificationProps) {
  const data = notification.data as RitualLiveNotificationData

  const ritualId = data.ritualId
  const ritualName = data.ritualName || "a ritual"
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isPaid = data.isPaid
  const fee = data.fee
  const duration = data.duration
  const durationType = data.durationType
  const ritualType = data.ritualType
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const priceDisplay = isPaid && fee ? `$${fee}` : "Free"
  const ritualTypeLabel = ritualType ? getRitualTypeLabel(ritualType) : ""

  function formatRitualDuration(dur: string | undefined, durType: string | undefined): string {
    if (!dur) return ""
    const mins = parseInt(dur, 10)
    if (!durType || durType === "minutes") return `${mins}min`
    if (durType === "hours") {
      const hrs = Math.floor(mins / 60)
      const remainingMins = mins % 60
      return remainingMins > 0 ? `${hrs}hrs ${remainingMins}min` : `${hrs}hrs`
    }
    if (durType === "days") {
      const days = Math.floor(mins / 1440)
      return `${days}days`
    }
    return `${mins}min`
  }

  const formattedDuration = formatRitualDuration(duration, durationType)

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/rituals/${ritualId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px] pr-20">
              Your ritual is now live and open for participants. People can discover it, join, and
              engage with your session in real time.
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Ritual Card */}
      <div
        className="relative mt-2 h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden cursor-pointer border border-card"
        onClick={handleRitualClick}
      >
        {ritualCoverImageUrl ? (
          <img src={ritualCoverImageUrl} alt={ritualName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {ritualName}
            </span>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span
                className={`text-white text-[13px] px-2 py-1 rounded-sm leading-[20px] ${isPaid ? "bg-brand-terracotta" : "bg-brand-olive"}`}
              >
                {priceDisplay}
              </span>
              <span className="text-white/80 text-[13px] leading-[20px]">{ritualTypeLabel}</span>
              <span className="text-white/60 text-[13px] leading-[20px]">{formattedDuration}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
